
import React, { useState } from 'react';
import { useUI } from '../contexts/UIProvider';
import * as api from '../services/apiService';
import ArrowLeftIcon from './icons/ArrowLeftIcon';

interface ForgotPasswordPageProps { onBack: () => void; }

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({ onBack }) => {
    const [handle, setHandle] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const { showToast } = useUI();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!handle) return;

        setIsLoading(true);
        try {
            await api.requestPasswordReset(handle.toLowerCase().replace('@', ''));
            setSent(true);
        } catch {
            showToast('Could not process request. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-brand-dark min-h-screen flex items-center justify-center p-4 font-urbanist">
            <div className="w-full max-w-md bg-brand-surface rounded-xl shadow-lg p-8 border border-gray-800">
                <div className="text-center mb-8">
                    <div className="flex justify-center">
                        <img src="/logo.png" alt="beYourAMP" className="h-12 w-auto" />
                    </div>

                    {sent ? (
                        <>
                            <div className="text-5xl mt-6 mb-3">📧</div>
                            <h2 className="text-xl font-black text-white uppercase tracking-tight">Check your email</h2>
                            <p className="text-brand-muted mt-2 text-sm">
                                If an account exists for that handle, we've sent a reset link to the registered email address. The link expires in 1 hour.
                            </p>
                            <button onClick={onBack} className="mt-6 w-full bg-brand-orange text-white font-black py-3 px-4 rounded-full hover:opacity-90 transition-opacity uppercase tracking-widest text-sm shadow-lg shadow-brand-orange/20">
                                Back to Sign In
                            </button>
                        </>
                    ) : (
                        <>
                            <h2 className="text-xl font-bold text-white mt-6">Reset Your Password</h2>
                            <p className="text-brand-muted mt-2 text-sm">
                                Enter your @handle and we'll send a reset link to your email address.
                            </p>

                            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-orange font-bold">@</span>
                                    <input
                                        type="text"
                                        placeholder="handle"
                                        value={handle}
                                        onChange={(e) => setHandle(e.target.value)}
                                        required
                                        className="w-full bg-gray-700 text-white rounded-md p-3 pl-10 focus:outline-none focus:ring-2 focus:ring-brand-orange font-mono font-bold"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isLoading || !handle}
                                    className="w-full bg-brand-orange text-white font-bold py-3 px-4 rounded-full hover:opacity-90 disabled:opacity-50 transition-opacity uppercase tracking-widest text-sm shadow-lg shadow-brand-orange/20"
                                >
                                    {isLoading
                                        ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
                                        : 'Send Reset Link'}
                                </button>
                            </form>

                            <div className="text-center mt-8 pt-4 border-t border-gray-800">
                                <button onClick={onBack} className="text-xs font-bold text-brand-muted hover:text-white flex items-center justify-center mx-auto uppercase tracking-widest">
                                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                                    Back to Sign In
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
